import styled from 'styled-components';

export enum VolumeCalculationDataType {
  Area = 'Area',
  Design = 'Design',
  PointCloud = 'Punktmoln',
  Dxf = 'CAD',
  Glb = 'CAD',
  TerrainModel = 'Mark',
}

export const pdfPageDimensions = {
  a4: {
    landscape72Dpi: {
      width: 842,
      height: 595,
      defaultPadding: 24,
    },
    portrait72Dpi: {
      width: 595,
      height: 842,
      defaultPadding: 24,
    },
  },
  a3: {
    landscape72Dpi: {
      height: 842,
      width: 1191,
      defaultPadding: 32,
    },
  },
};

export type PaperSize = 'A4' | 'A3' | 'A2' | 'A1' | 'A0';
export type PageOrientation = 'portrait' | 'landscape';
export type PageLayout = 'cad-frame-1';

/** A size on paper in millimeters. */
export type PaperDimensions = { width: number; height: number };

export const paperSizes: PaperSize[] = ['A4', 'A3', 'A2', 'A1', 'A0'];

/**
 * The page layouts of the backend's CAD drawing report, portrait. The map area is what the frame in
 * the viewer covers. These mirror the backend's definitions, which refuse a frame whose aspect ratio
 * is off by more than 0.5 %.
 *
 * cad-frame-1: a frame 10 mm inside the paper edge, with the title block over the map in its
 * bottom-right corner. The map area is the inside of the frame, title block included.
 */
export const pageLayouts: Record<
  PageLayout,
  Record<PaperSize, { paper: PaperDimensions; mapArea: PaperDimensions }>
> = {
  'cad-frame-1': {
    A4: { paper: { width: 210, height: 297 }, mapArea: { width: 190, height: 277 } },
    A3: { paper: { width: 297, height: 420 }, mapArea: { width: 277, height: 400 } },
    A2: { paper: { width: 420, height: 594 }, mapArea: { width: 400, height: 574 } },
    A1: { paper: { width: 594, height: 841 }, mapArea: { width: 574, height: 821 } },
    A0: { paper: { width: 841, height: 1189 }, mapArea: { width: 821, height: 1169 } },
  },
};

/** The size of a page layout's map area in millimeters. Landscape swaps width and height. */
export function getMapAreaMm(
  layout: PageLayout,
  size: PaperSize,
  orientation: PageOrientation,
): PaperDimensions {
  const { width, height } = pageLayouts[layout][size].mapArea;
  return orientation === 'portrait' ? { width, height } : { width: height, height: width };
}

export const StickySearchBox = styled.div<{ height: number }>`
  position: -webkit-sticky; /* For Safari */
  position: sticky;
  top: ${(props) => props.height}px;
  z-index: 1;
  padding-top: 0.2em;
  background-color: white;
  box-shadow:
    0 8px 8px -4px rgba(0, 0, 0, 0.12),
    0 12px 12px -6px rgba(0, 0, 0, 0.24);

  // Adds white edges "borders" to the left and right of the search box.
  // Otherwise the box shadow of the elemens that scroll underneath it will be visible
  // behind the search box which does not look good.
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 0.2em;
    background-color: #fff;
  }

  &::before {
    left: -0.2em; // make sure this matches with content width
  }

  &::after {
    right: -0.2em; // make sure this matches with content width
  }
`;
